import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { UI_THEME } from '../../config/constants';
import { ExcelProcessingService } from '../../services/excelProcessingService';
import { PubSubService } from '../../services/pubSubService';
import { useAuth } from '../../hooks/useAuth';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  acceptedFileTypes?: string[];
  maxFileSize?: number; // in MB
  className?: string;
  style?: React.CSSProperties;
}

const DEFAULT_ACCEPTED_TYPES = ['.xlsx', '.xls'];
const DEFAULT_MAX_FILE_SIZE = 10; // 10MB

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  className = '',
  style = {},
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>('');

  const validateFile = async (file: File): Promise<string | null> => {
    // Check file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedFileTypes.includes(fileExtension)) {
      return `Unsupported file type. Please upload ${acceptedFileTypes.join(', ')} files only.`;
    }

    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxFileSize) {
      return `File size too large. Maximum size allowed is ${maxFileSize}MB.`;
    }

    // Validate Excel columns
    if (fileExtension === '.xlsx' || fileExtension === '.xls') {
      try {
        const columnValidation = await validateExcelColumns(file);
        if (columnValidation) {
          return columnValidation;
        }
      } catch (error) {
        return 'Error reading Excel file. Please ensure the file is not corrupted.';
      }
    }

    return null;
  };

  const validateExcelColumns = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON to get column headers
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length === 0) {
            resolve('Excel file appears to be empty.');
            return;
          }

          // Get the first row (headers)
          const headers = (jsonData[0] as string[]).map(header =>
            header?.toString().trim().toLowerCase()
          );

          const requiredColumns = ['company', 'geography'];
          const missingColumns = requiredColumns.filter(col =>
            !headers.includes(col)
          );

          if (missingColumns.length > 0) {
            const missingFormatted = missingColumns.map(col =>
              col.charAt(0).toUpperCase() + col.slice(1)
            );
            resolve(
              `Missing required column${missingColumns.length > 1 ? 's' : ''}: ${missingFormatted.join(', ')}. Please ensure your Excel file contains "Company" and "Geography" columns.`
            );
            return;
          }

          resolve(null); // No errors
        } catch (error) {
          resolve('Error reading Excel file. Please ensure the file is valid.');
        }
      };

      reader.onerror = () => {
        resolve('Error reading file.');
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileSelection = async (file: File) => {
    setError(null);
    setSuccess(null);
    setIsValidating(true);
    setIsProcessing(false);
    setProcessingStatus('');

    // First, validate the file format and columns
    const validationError = await validateFile(file);
    setIsValidating(false);

    if (validationError) {
      setError(validationError);
      return;
    }

    // Process Excel file and send to Pub/Sub
    try {
      setIsProcessing(true);
      setProcessingStatus('Processing Excel file...');

      // Process the Excel file
      const result = await ExcelProcessingService.processExcelFile(file);

      if (result.errors.length > 0) {
        console.warn('Excel processing warnings:', result.errors);
      }

      if (result.companies.length === 0) {
        setError('No valid companies found in Excel file. Please check the data and try again.');
        setIsProcessing(false);
        return;
      }

      setProcessingStatus(`Found ${result.companies.length} companies. Sending to processing queue...`);

      // Note: Status records will be created by the backend when processing Pub/Sub messages
      // No need to create them here to avoid duplicates

      // Send companies to Pub/Sub via Firebase Cloud Function
      const publishResult = await PubSubService.publishCompanies(result.companies, user!.uid);
      console.log('🔄 FileUpload: Pub/Sub publish result:', publishResult);

      setIsProcessing(false);

      if (publishResult.failed.length > 0) {
        const errorMessage = `Successfully queued ${publishResult.successful.length} companies for processing. ${publishResult.failed.length} companies failed to queue.`;
        setError(errorMessage);
        console.error('Failed to publish companies:', publishResult.failed);
      } else {
        const successMessage = `Successfully queued ${publishResult.successful.length} companies for lead generation! Each company will be processed asynchronously.`;
        setSuccess(successMessage);
      }

      // Call the original onFileSelect for any additional handling
      onFileSelect(file);

    } catch (error) {
      setIsProcessing(false);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to process Excel file: ${errorMessage}`);
      console.error('Excel processing error:', error);
    }
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const baseStyles: React.CSSProperties = {
    border: `2px dashed ${isDragOver ? UI_THEME.colors.primary : UI_THEME.colors.gray[300]}`,
    borderRadius: UI_THEME.borderRadius.md,
    padding: UI_THEME.spacing.lg,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: isDragOver ? '#f0f9ff' : UI_THEME.colors.background.primary,
    ...style,
  };

  return (
    <div className={className}>
      <div
        style={baseStyles}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInput}
          accept={acceptedFileTypes.join(',')}
          style={{ display: 'none' }}
        />

        <div style={{ marginBottom: UI_THEME.spacing.md }}>
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke={UI_THEME.colors.gray[400]}
            strokeWidth="2"
            style={{ margin: '0 auto', display: 'block' }}
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14,2 14,8 20,8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10,9 9,9 8,9" />
          </svg>
        </div>

        <h3 style={{
          fontSize: UI_THEME.fontSize.base,
          fontWeight: UI_THEME.fontWeight.medium,
          color: UI_THEME.colors.gray[700],
          marginBottom: UI_THEME.spacing.sm,
        }}>
          Upload Companies File
        </h3>

        <p style={{
          fontSize: UI_THEME.fontSize.sm,
          color: UI_THEME.colors.gray[500],
          marginBottom: UI_THEME.spacing.sm,
        }}>
          Drag and drop your file here, or click to browse
        </p>

        <div style={{
          fontSize: UI_THEME.fontSize.sm,
          color: UI_THEME.colors.gray[400],
          textAlign: 'center',
        }}>
          <p style={{ marginBottom: UI_THEME.spacing.xs }}>
            Supported formats: {acceptedFileTypes.join(', ')} • Max size: {maxFileSize}MB
          </p>
          <p style={{
            fontSize: '12px',
            color: UI_THEME.colors.gray[500],
            fontStyle: 'italic'
          }}>
            Excel file must contain "Company" and "Geography" columns
          </p>
          <p style={{
            fontSize: '12px',
            color: UI_THEME.colors.gray[500],
            fontStyle: 'italic'
          }}>
            Maximum 50 companies allowed per file
          </p>
        </div>
      </div>

      {error && (
        <div style={{
          marginTop: UI_THEME.spacing.sm,
          padding: UI_THEME.spacing.md,
          backgroundColor: UI_THEME.colors.background.error,
          border: `1px solid ${UI_THEME.colors.background.errorBorder}`,
          borderRadius: UI_THEME.borderRadius.sm,
          display: 'flex',
          alignItems: 'center',
          gap: UI_THEME.spacing.sm,
        }}>
          <svg width="20" height="20" fill={UI_THEME.colors.error} viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span style={{
            color: UI_THEME.colors.error,
            fontSize: UI_THEME.fontSize.sm,
            fontWeight: UI_THEME.fontWeight.medium,
          }}>
            {error}
          </span>
        </div>
      )}

      {(isValidating || isProcessing) && (
        <div style={{
          marginTop: UI_THEME.spacing.sm,
          padding: UI_THEME.spacing.md,
          backgroundColor: '#fef3c7',
          border: '1px solid #fcd34d',
          borderRadius: UI_THEME.borderRadius.sm,
          display: 'flex',
          alignItems: 'center',
          gap: UI_THEME.spacing.sm,
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid rgba(245, 158, 11, 0.3)',
            borderTop: '2px solid #f59e0b',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{
            color: '#f59e0b',
            fontSize: UI_THEME.fontSize.sm,
            fontWeight: UI_THEME.fontWeight.medium,
          }}>
            {isValidating ? 'Validating Excel file...' : processingStatus || 'Processing...'}
          </span>
        </div>
      )}

      {success && !isValidating && !isProcessing && (
        <div style={{
          marginTop: UI_THEME.spacing.sm,
          padding: UI_THEME.spacing.md,
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: UI_THEME.borderRadius.sm,
          display: 'flex',
          alignItems: 'center',
          gap: UI_THEME.spacing.sm,
        }}>
          <svg width="20" height="20" fill={UI_THEME.colors.success} viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span style={{
            color: UI_THEME.colors.success,
            fontSize: UI_THEME.fontSize.sm,
            fontWeight: UI_THEME.fontWeight.medium,
          }}>
            {success}
          </span>
        </div>
      )}
    </div>
  );
};